import { defineStore } from "pinia";
import { supabase } from "@/supabase";
import { getEffectiveUserId, isImpersonating } from "@/utils/impersonation";
import { fetchFacebookPageId, updateFacebookPageId } from "@/api/social";

export const useFacebookPageIdStore = defineStore("facebookPageId", {
  state: () => ({
    pageId: "",
    status: "idle",
    error: "",
    loaded: false,
    loadPromise: null,
    updatePromise: null,
    loadingEffectiveUserId: null,
    loadingIsImpersonating: false,
    lastLoadedEffectiveUserId: null,
    isImpersonating: false,
  }),
  actions: {
    applyBootstrapPageId(pageId, session = null) {
      if (!session?.user?.id) {
        this.clear();
        return "";
      }

      const effectiveUserId = getEffectiveUserId(session.user.id);
      const nextIsImpersonating = isImpersonating();
      this.pageId = String(pageId || "").trim();
      this.status = "ready";
      this.error = "";
      this.loaded = true;
      this.lastLoadedEffectiveUserId = effectiveUserId;
      this.isImpersonating = nextIsImpersonating;
      return this.pageId;
    },
    async resolveSession(session = null) {
      if (session) {
        return session;
      }
      const {
        data: { session: currentSession },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }
      return currentSession;
    },
    async fetchPageId(session = null, { force = false } = {}) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.user?.id) {
        this.clear();
        return "";
      }

      const effectiveUserId = getEffectiveUserId(currentSession.user.id);
      const nextIsImpersonating = isImpersonating();
      if (
        !force
        && this.loaded
        && this.lastLoadedEffectiveUserId === effectiveUserId
        && this.isImpersonating === nextIsImpersonating
      ) {
        return this.pageId;
      }
      if (
        this.loadPromise
        && this.loadingEffectiveUserId === effectiveUserId
        && this.loadingIsImpersonating === nextIsImpersonating
      ) {
        return this.loadPromise;
      }

      this.status = "loading";
      this.error = "";
      this.loadingEffectiveUserId = effectiveUserId;
      this.loadingIsImpersonating = nextIsImpersonating;

      this.loadPromise = (async () => {
        const payload = await fetchFacebookPageId(currentSession);

        this.pageId = String(payload?.page_id || "").trim();
        this.loaded = true;
        this.lastLoadedEffectiveUserId = effectiveUserId;
        this.isImpersonating = nextIsImpersonating;
        this.status = "ready";
        this.error = "";
        return this.pageId;
      })().catch((error) => {
        this.status = "error";
        this.error = error?.message || String(error);
        throw error;
      }).finally(() => {
        this.loadPromise = null;
        this.loadingEffectiveUserId = null;
        this.loadingIsImpersonating = false;
      });

      return this.loadPromise;
    },
    async refreshPageId(session = null) {
      return this.fetchPageId(session, { force: true });
    },
    async updatePageId(nextPageId, session = null) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.user?.id) {
        this.clear();
        return "";
      }

      const trimmedPageId = String(nextPageId || "").trim();
      this.error = "";
      this.updatePromise = (async () => {
        const payload = await updateFacebookPageId(currentSession, trimmedPageId);

        const effectiveUserId = getEffectiveUserId(currentSession.user.id);
        const nextIsImpersonating = isImpersonating();
        this.pageId = String(payload?.page_id || "").trim();
        this.loaded = true;
        this.lastLoadedEffectiveUserId = effectiveUserId;
        this.isImpersonating = nextIsImpersonating;
        this.status = "ready";
        this.error = "";
        return this.pageId;
      })().catch((error) => {
        this.status = "error";
        this.error = error?.message || String(error);
        throw error;
      }).finally(() => {
        this.updatePromise = null;
      });

      return this.updatePromise;
    },
    clear() {
      this.pageId = "";
      this.status = "idle";
      this.error = "";
      this.loaded = false;
      this.loadPromise = null;
      this.updatePromise = null;
      this.loadingEffectiveUserId = null;
      this.loadingIsImpersonating = false;
      this.lastLoadedEffectiveUserId = null;
      this.isImpersonating = false;
    },
  },
});
